import { CommonModule, DatePipe, formatDate } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, FormsModule, NgControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BlockUI, BlockUIModule, NgBlockUI } from 'ng-block-ui';
import { LessonPlan } from 'src/app/_models/lessonPlan';
import { Member } from 'src/app/_models/member';
import { PlanMember } from 'src/app/_models/planMember';
import { AlertifyService } from 'src/app/_services/alertify.service';
import { AuthService } from 'src/app/_services/auth.service';
import { PlanService } from 'src/app/_services/plan.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatCheckbox } from '@angular/material/checkbox';

interface Option {
  value: string;
  display: string;
}

@Component({
  selector: 'app-editLessonPlan',
  // standalone: true,
  // imports: [ CommonModule, BlockUIModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, FormsModule, MatInputModule, MatCheckbox ],
  templateUrl: './editLessonPlan.component.html',
  styleUrls: ['./editLessonPlan.component.css']
})
export class EditLessonPlanComponent implements OnInit {
  @BlockUI() blockUI: NgBlockUI;
  member: Member;
  lessonPlan : LessonPlan;

  editPlanMemberIndex: number = -1;
  editPlanStepIndex: number = -1;

  submitted: boolean = false;

  TITLE_MIN_LENGTH = 6;
  TITLE_MAX_LENGTH = 100;

  itemTypes: Option[] = [
    {value: 'LABEL', display: 'Label'},
    {value: 'Pattern', display: 'Pattern'}
  ];

  form: FormGroup = new FormGroup({
    title: new FormControl(''),
    description: new FormControl(''),
    dueDate: new FormControl('')
  });
  
  constructor(private route: ActivatedRoute
    , private router: Router
    , private alertify: AlertifyService
    , private planService: PlanService
    , private authService: AuthService
    , private formBuilder: FormBuilder
    , public datePipe: DatePipe) { }

  ngOnInit() {
    this.blockUI.start("Loading lesson plan");
    this.member = this.authService.currentMember;
    this.route.params.subscribe((params) => {
      if (params) {
        const lessonPlanId = params.id;
        if (isNaN(+lessonPlanId) || +lessonPlanId < 0) {
          this.alertify.error("LessonPlanID " + lessonPlanId + " is not valid");
        } else {
          this.planService.getLessonPlan(lessonPlanId).subscribe(lessonPlan => {
            var planMember = lessonPlan.planMembers.filter(pm => pm.accepted && pm.canUpdate && pm.member && pm.member.id == this.member.id);
            if (lessonPlan.member.id != this.member.id && planMember.length === 0)
            {
              this.router.navigate(['home']);
            }
            this.lessonPlan = lessonPlan;
            this.loadLessonPlan(lessonPlan);
          }, error => {
            this.alertify.error(error);
          });
        }        
      }      
     });
     this.blockUI.stop();
  }

  loadLessonPlan(lessonPlan: LessonPlan) {
    this.form = this.formBuilder.group(
      {
        id: [ this.lessonPlan.id],
        memberId: [ this.lessonPlan.member.id], 
        title: [ this.lessonPlan.title
          // ,[
          //   Validators.required,
          //   Validators.minLength(this.TITLE_MIN_LENGTH),
          //   Validators.maxLength(this.TITLE_MAX_LENGTH)
          // ]
        ],
        description: [ lessonPlan.description
          // ,[
          //   Validators.required
          // ]
        ],
        isActive: [ this.lessonPlan.isActive ],
        dueDate: [ formatDate(lessonPlan.dueDate,'yyyy-MM-dd','en')
          // , [
          //   Validators.required
          // ]
        ],
        planMembers: this.formBuilder.array([]),
        planItems: this.formBuilder.array([])
      }
    );
    this.loadPlanMembers();
    this.loadPlanItems();
  }

  loadPlanMembers() {
    const control = <FormArray>this.form.get('planMembers');
    this.lessonPlan.planMembers?.forEach(x => {
      const planMember = this.formBuilder.group(
        {
          id: [ x.id ],
          memberId: [ x.member?.id ],
          emailAddress: [x.emailAddress
            // , Validators.required
          ],
          canUpdate: [x.canUpdate
            // , Validators.required
          ],
          accepted: [ x.accepted ]
        }
      )
      control.push(planMember)
    });
  }

  loadPlanItems() {
    const control = <FormArray>this.form.get('planItems');
    this.lessonPlan.planItems?.forEach(x => {
      const planItem = this.formBuilder.group(
        {
          id: [ x.id ],
          sequence: [ x.sequence
            // , Validators.required 
          ],
          type: [ x.type ],
          isActive: [ x.isActive
            // , Validators.required 
          ],
          value: [ x.value
            // , Validators.required 
          ],
          itemNotes: this.formBuilder.array([]),
          notesCount: [ x.itemNotes.length ]
        }
      )
      const planItems = <FormArray>planItem.get('itemNotes');
      x.itemNotes.forEach(note => {
        const noteGroup = this.formBuilder.group(
          {
            id: [ note.id ],
            member: [ note.member ],
            value: [ note.value ]
          }
        );        
        planItems.push(noteGroup);
      });
      control.push(planItem)
    });
  }

  get planMembers() {
    return this.form.controls["planMembers"] as FormArray;
  }

  get planItems() {
    return this.form.controls["planItems"] as FormArray;
  }

  addPlanMember() {
    const member = this.formBuilder.group({
      emailAddress: [''
        // , Validators.required
      ],
      canUpdate: [false
        // , Validators.required
      ],
      accepted: [ null ]
    });
    this.planMembers.push(member);
    this.editPlanMemberIndex = this.planMembers.length -1;
  }
  
  addPlanItem() {
    var lastSequence = 0;
    if (this.planItems.length > 0) {
      const lastPlanItem = this.planItems.at(this.planItems.length - 1) as FormGroup;
      lastSequence = lastPlanItem.controls.sequence.value;
    }
    
    const item = this.formBuilder.group({
      sequence: [ lastSequence + 1
        // , Validators.required 
      ],
      type: [ ''
        // , Validators.required 
      ],
      isActive: [ true],
      value: [ ''
        // , Validators.required 
      ]
    });
    this.planItems.push(item);
    this.editPlanStepIndex = this.planItems.length - 1;
  }

  deletePlanMember(index: number) {
    this.planMembers.removeAt(index);
  }
  deletePlanItem(index: number) {
    this.planItems.removeAt(index);
  }

  get f(): { [key: string]: AbstractControl } {
    return this.form.controls;
  }

  moveStep(index: number, direction: number)
  {
    const editedStep = this.planItems.at(index) as FormGroup;
    const movedStep = this.planItems.at(index + direction) as FormGroup;

    const editedSequence = movedStep.controls.sequence.value;
    const movedSequence = editedStep.controls.sequence.value;
    
    editedStep.patchValue ( { sequence: editedSequence });
    movedStep.patchValue ( { sequence: movedSequence });

  }

  onSubmit(event : string): void {
    this.submitted = true;
    if (this.form.invalid) {
      this.alertify.error("Form is invalid");
      return;
    }

    this.blockUI.start("Submitting changes - please wait");
    var lessonPlan = this.form.value;

    lessonPlan.sendEmail = false;
    if (event == "emailChanges") {
      lessonPlan.sendEmail = true;
    }
    this.planService.updateLessonPlan(lessonPlan).subscribe(plan => {
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate(['/lessonPlan/edit/' + plan.id])});
    }, error => {
      this.alertify.error(error);
      this.blockUI.stop();
    });
    
  }

  onReset(): void {
    this.submitted = false;
    this.form.reset(this.lessonPlan);
  }
}
